import React, { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  query,
  getDocs,
  collection,
  setDoc,
  startAt,
  endAt,
  orderBy,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { Calendar, ClipboardList, Filter, List } from "lucide-react";
import { Helmet } from "react-helmet";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "../hooks/AuthenticationProvider";

// List of all possible substances with their full names
const ALL_SUBSTANCES = {
  AMP: { name: "Amphetamine", value: "", type: "drug" },
  BUP: { name: "Buprenorphine", value: "", type: "drug" },
  BZO: { name: "Benzodiazepines", value: "", type: "drug" },
  COC: { name: "Cocaine", value: "", type: "drug" },
  MET: { name: "Methamphetamine", value: "", type: "drug" },
  MTD: { name: "Methadone", value: "", type: "drug" },
  OPI: { name: "Opiates/Morphine", value: "", type: "drug" },
  OXY: { name: "Oxycodone", value: "", type: "drug" },
  THC: { name: "Marijuana", value: "", type: "drug" },
  PCP: { name: "Phencyclidine", value: "", type: "drug" },
  GAB: { name: "Gabapentin", value: "", type: "drug" },
  ETG: { name: "Alcohol", value: "", type: "drug" },
  FEN: { name: "Fentanyl", value: "", type: "drug" },
  K2: { name: "Synthetic Cannabinoids", value: "", type: "drug" },
  Kratom: { name: "Kratom", value: "", type: "drug" },
  BAR: { name: "Barbituates", value: "", type: "drug" },
  KET: { name: "Ketamine", value: "", type: "drug" },
  LSD: { name: "LSD/Acid", value: "", type: "drug" },
  MPD: { name: "Methylphenidate", value: "", type: "drug" },
  PY: { name: "Psilocybin", value: "", type: "drug" },
  TML: { name: "Tramadol", value: "", type: "drug" },
  TCA: { name: "Tricylic Antidepressants", value: "", type: "drug" },
  "a-PVP": { name: "Flakka", value: "", type: "drug" },
  XYL: { name: "Xylazine", value: "", type: "drug" },
  TIA: { name: "Tianeptine (ZAZA)", value: "", type: "drug" },
  // Adulterants
  PH: { name: "pH", value: "", type: "adulterant" },
  SG: { name: "Specific Gravity", value: "", type: "adulterant" },
  CREA: { name: "Creatine", value: "", type: "adulterant" },
};

const UA = () => {
  const {
    organizationId,
    organizationData,
    checkPermission,
    checkFeature,
    isLoading,
  } = useAuth();
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [weekNumber, setWeekNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Default to "collect" if uaScheduling feature is disabled
    return checkFeature("uaScheduling") ? "schedule" : "collect";
  });
  const [historyResults, setHistoryResults] = useState([]);
  const [submittedUAs, setSubmittedUAs] = useState([]);
  const [selectedResident, setSelectedResident] = useState("");
  const [uaResults, setUaResults] = useState({});
  const [showBreathalyzerModal, setShowBreathalyzerModal] = useState(false);
  const [bacLevel, setBacLevel] = useState("");

  // New state for substance list modal
  const [showSubstanceModal, setShowSubstanceModal] = useState(false);
  const [availableSubstances, setAvailableSubstances] = useState({});
  const [tempSubstances, setTempSubstances] = useState({});

  const normalizeName = (name) => name.replace(/\s\(\d+\)$/, "").trim();

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const tabs = [
    ...(checkFeature("uaScheduling")
      ? [
          {
            id: "schedule",
            label: "Schedule",
            icon: <Calendar className="w-4 h-4" />,
            permission: "viewUa",
          },
        ]
      : []),
    ...(checkFeature("uaRecording")
      ? [
          {
            id: "collect",
            label: "Collect",
            icon: <ClipboardList className="w-4 h-4" />,
            permission: "addUa",
          },
        ]
      : []),
    {
      id: "history",
      label: "History",
      icon: <Filter className="w-4 h-4" />,
      permission: "viewUaHistory",
    },
  ];

  const renderBreathalyzerModal = () => {
    if (!showBreathalyzerModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-900 rounded-xl w-full max-w-md shadow-2xl">
          <div className="p-6 border-b border-slate-700">
            <h3 className="text-xl font-bold text-white">
              Record Breathalyzer
            </h3>
            <p className="text-slate-400 text-sm mt-1">{selectedResident}</p>
          </div>

          <div className="p-6">
            <label htmlFor="bac-level" className="block text-slate-400 mb-2">
              BAC Level
            </label>
            <input
              id="bac-level"
              type="number"
              step="0.001"
              min="0"
              max="1"
              value={bacLevel}
              onChange={(e) => setBacLevel(e.target.value)}
              placeholder="0.000"
              className="w-full px-4 py-3 bg-slate-800/50 backdrop-blur-xl text-white rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-all"
            />
            <p className="text-xs text-slate-500 mt-1">
              Enter the blood alcohol concentration (BAC) level (e.g., 0.08)
            </p>
          </div>

          <div className="p-6 border-t border-slate-700 flex flex-wrap gap-4 justify-end">
            <button
              onClick={() => setShowBreathalyzerModal(false)}
              className="px-5 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleBreathalyzerSubmit}
              disabled={isSubmitting}
              className={`px-5 py-2 ${
                isSubmitting
                  ? "bg-gray-500 cursor-not-allowed"
                  : "bg-cyan-600 hover:bg-cyan-500"
              } text-white rounded-lg transition`}
            >
              {isSubmitting ? "Submitting..." : "Submit Result"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Fetch House Settings for Substance List
  useEffect(() => {
    const fetchHouseSettings = async () => {
      if (!organizationId) return;

      try {
        const houseSettingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );
        const houseSettingsDoc = await getDoc(houseSettingsRef);

        if (houseSettingsDoc.exists()) {
          const data = houseSettingsDoc.data();

          // If substances already exist in the settings, use them
          if (data.substances && Object.keys(data.substances).length > 0) {
            setAvailableSubstances(data.substances);

            // Initialize uaResults with the available substances set to false
            const initialResults = {};
            Object.keys(data.substances).forEach((key) => {
              initialResults[key] = false;
            });
            setUaResults(initialResults);
          } else {
            // Default to all substances checked if none are set
            const allSubstancesChecked = {};
            Object.keys(ALL_SUBSTANCES).forEach((key) => {
              allSubstancesChecked[key] = true;
            });
            setAvailableSubstances(allSubstancesChecked);

            // Initialize uaResults with all substances set to false
            const initialResults = {};
            Object.keys(allSubstancesChecked).forEach((key) => {
              initialResults[key] = false;
            });
            setUaResults(initialResults);
          }
        } else {
          // If document doesn't exist, default to all substances checked
          const allSubstancesChecked = {};
          Object.keys(ALL_SUBSTANCES).forEach((key) => {
            allSubstancesChecked[key] = true;
          });
          setAvailableSubstances(allSubstancesChecked);

          // Initialize uaResults with all substances set to false
          const initialResults = {};
          Object.keys(allSubstancesChecked).forEach((key) => {
            initialResults[key] = false;
          });
          setUaResults(initialResults);
        }
      } catch (err) {
        console.error("Error fetching house settings:", err);
      }
    };

    fetchHouseSettings();
  }, [organizationId]);

  // Fetch UA Schedule
  useEffect(() => {
    const fetchUASchedule = async () => {
      if (!organizationId || !organizationData?.housesData) return;

      try {
        const scheduleDoc = await getDoc(
          doc(db, `organizations/${organizationId}/uaSchedule/currentWeek`)
        );
        if (scheduleDoc.exists()) {
          const data = scheduleDoc.data();
          const schedule = {};
          Object.keys(organizationData.housesData).forEach((house) => {
            if (data.schedule && data.schedule[house]) {
              schedule[house] = data.schedule[house];
            } else {
              schedule[house] = {
                Monday: [],
                Tuesday: [],
                Wednesday: [],
                Thursday: [],
                Friday: [],
                Saturday: [],
                Sunday: [],
              };
            }
          });

          setWeeklySchedule(schedule);
          setWeekNumber(data.weekNumber || 1);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching UA schedule:", err);
        setLoading(false);
      }
    };

    fetchUASchedule();
  }, [organizationId, organizationData?.housesData]);

  // Fetch Submitted UAs
  // Fetch Submitted UAs
  useEffect(() => {
    const fetchSubmittedUAs = () => {
      if (!organizationId) return;

      // Create an array to store all the promises
      const fetchPromises = [];

      // Loop through all current residents
      (organizationData?.residents || []).forEach((resident) => {
        if (resident.currentResident && resident.id) {
          // Create a query for this resident's UA results
          const q = query(
            collection(
              db,
              `organizations/${organizationId}/residents/${resident.id}/uaResults`
            ),
            orderBy("date", "desc")
          );

          // Create a promise for this resident's query
          const fetchPromise = getDocs(q).then((snapshot) => {
            return snapshot.docs.map((doc) => doc.data());
          });

          fetchPromises.push(fetchPromise);
        }
      });

      // Execute all promises and combine the results
      Promise.all(fetchPromises)
        .then((results) => {
          // Flatten the array of arrays into a single array
          const allSubmittedData = results.flat();

          // Sort by date descending
          allSubmittedData.sort((a, b) => new Date(b.date) - new Date(a.date));

          setSubmittedUAs(allSubmittedData);
        })
        .catch((error) => {
          console.error("Error fetching submitted UAs:", error);
        });
    };

    if (organizationId && organizationData?.residents) {
      fetchSubmittedUAs();
    }
  }, [organizationId, organizationData?.residents]);

  const handleBreathalyzerSubmit = async () => {
    if (isSubmitting || !selectedResident) {
      if (!selectedResident) alert("Please select a resident");
      return;
    }

    if (!bacLevel) {
      alert("Please enter a BAC level");
      return;
    }

    try {
      setIsSubmitting(true);
      const now = new Date();
      // Ensure we're using local timezone for the date
      const localNow = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      );

      // Find resident ID from the name
      const selectedResidentObj = organizationData?.residents.find(
        (resident) => resident.fullName === selectedResident
      );

      if (!selectedResidentObj || !selectedResidentObj.id) {
        alert("Could not find resident ID. Please try again.");
        setIsSubmitting(false);
        return;
      }

      const resultDocument = {
        residentFullName: selectedResident,
        date: localNow.toISOString(),
        weekIdentifier: `Week-${weekNumber}-${localNow.getFullYear()}`,
        type: "breathalyzer",
        bacLevel: bacLevel,
        results: {
          Breathalyzer: parseFloat(bacLevel) > 0 ? "fail" : "pass",
        },
        summary:
          parseFloat(bacLevel) > 0
            ? `Breathalyzer: Fail (${bacLevel})`
            : "Breathalyzer: Clean",
      };

      // New path using resident subcollection
      const breathalyzerRef = doc(
        db,
        `organizations/${organizationId}/residents/${
          selectedResidentObj.id
        }/uaResults/${selectedResident}-breathalyzer-${Date.now()}`
      );

      await setDoc(breathalyzerRef, resultDocument);

      setSubmittedUAs((prev) => [...prev, resultDocument]);
      alert("Breathalyzer result submitted successfully");
      setSelectedResident("");
      setBacLevel("");
      setShowBreathalyzerModal(false);
      setActiveTab("schedule");
    } catch (err) {
      console.error("Error submitting breathalyzer result:", err);
      alert("Failed to submit breathalyzer result");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLocalDateString = (dateString) => {
    const date = new Date(dateString);
    const localDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000
    );
    return localDate.toISOString().split("T")[0];
  };

  // Fetch UA history
  // Fetch UA history
  const fetchHistory = async (residentName, startDate, endDate) => {
    if (!organizationId || !residentName) return;

    try {
      // First, find the resident ID from the name
      const selectedResidentObj = organizationData?.residents.find(
        (resident) => resident.fullName === residentName
      );

      if (!selectedResidentObj || !selectedResidentObj.id) {
        console.error("Could not find resident ID for:", residentName);
        setHistoryResults([]);
        return;
      }

      // Now use the resident's subcollection
      const uaResultsRef = collection(
        db,
        `organizations/${organizationId}/residents/${selectedResidentObj.id}/uaResults`
      );

      let q;

      if (startDate && endDate) {
        const start = new Date(startDate).toISOString();
        const end = new Date(
          new Date(endDate).setHours(23, 59, 59)
        ).toISOString();

        q = query(uaResultsRef, orderBy("date"), startAt(start), endAt(end));
      } else {
        q = query(uaResultsRef, orderBy("date", "desc"));
      }

      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs.map((doc) => doc.data());

      setHistoryResults(results);
    } catch (err) {
      console.error("Error fetching UA history:", err);
      alert("Failed to fetch history. Please try again later.");
    }
  };

  const handleResidentSelection = async (e) => {
    const residentName = e.target.value;
    console.log("Selected resident:", residentName);
    setSelectedResident(residentName);

    if (residentName) {
      await fetchHistory(residentName, startDate, endDate);
    } else {
      setHistoryResults([]);
    }
  };

  const handleFilter = () => {
    if (!selectedResident) {
      alert("Please select a resident before applying filters.");
      return;
    }
    fetchHistory(selectedResident, startDate, endDate);
  };

  const generatePDF = () => {
    const doc = new jsPDF();

    // Check if we have organization header data
    if (
      organizationData?.houseSettings?.headerLogo ||
      organizationData?.houseSettings?.headerTitle ||
      organizationData?.houseSettings?.headerContact
    ) {
      let yPosition = 10;

      // Add logo if exists with full width
      if (organizationData.houseSettings.headerLogo) {
        try {
          // Calculate the width of the page (with margins)
          const pageWidth = doc.internal.pageSize.width - 28; // 14 units margin on each side
          const logoHeight = 30; // Adjusted height for better appearance

          doc.addImage(
            organizationData.houseSettings.headerLogo,
            "JPEG",
            14,
            yPosition,
            pageWidth,
            logoHeight
          );
          yPosition += logoHeight + 8; // Add spacing after logo
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      // Add organization name if exists
      if (organizationData.houseSettings.headerTitle) {
        doc.setFontSize(18); // Larger font for title
        doc.setFont(undefined, "bold");
        doc.text(organizationData.houseSettings.headerTitle, 14, yPosition);
        yPosition += 10;
      }

      // Add contact info if exists
      if (organizationData.houseSettings.headerContact) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        const contactLines =
          organizationData.houseSettings.headerContact.split("\n");
        contactLines.forEach((line) => {
          doc.text(line, 14, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Add divider line
      doc.setLineWidth(0.5);
      doc.line(14, yPosition, doc.internal.pageSize.width - 14, yPosition);
      yPosition += 15; // Add more space after divider

      // Add title with updated position - more prominent
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      const title = `UA & Breathalyzer History for ${selectedResident}`;
      doc.text(title, 14, yPosition);

      // Add date range if filtering
      if (startDate && endDate) {
        yPosition += 8;
        doc.setFontSize(10);
        doc.setFont(undefined, "italic");
        const dateRangeText = `Date Range: ${new Date(
          startDate
        ).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`;
        doc.text(dateRangeText, 14, yPosition);
      }

      // Process history results to create better formatted table data
      const tableData = historyResults.map((result) => {
        // Format date nicely
        const date = new Date(result.date);
        const formattedDate = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });

        // For breathalyzer results
        if (result.type === "breathalyzer") {
          const status = parseFloat(result.bacLevel) > 0 ? "Failed" : "Clean";
          return [
            formattedDate,
            "Breathalyzer",
            `BAC Level: ${result.bacLevel} - ${status}`,
          ];
        }

        // Create separate lists of passed and failed substances
        const passedSubstances = [];
        const failedSubstances = [];

        Object.entries(result.results)
          .filter(([substance]) => availableSubstances[substance] === true) // Only include enabled substances
          .forEach(([substance, status]) => {
            const substanceName =
              getSubstanceDisplayName(substance).toUpperCase();

            if (status.toLowerCase() === "pass") {
              passedSubstances.push(substanceName);
            } else if (status.toLowerCase() === "fail") {
              failedSubstances.push(substanceName);
            }
          });

        // Format the details text with clear separation
        let details = "";

        if (passedSubstances.length > 0) {
          details += `PASSED: ${passedSubstances.join(", ")}`;
        }

        if (failedSubstances.length > 0) {
          if (details) details += "\n\n";
          details += `FAILED: ${failedSubstances.join(", ")}`;
        }

        // Create a clear status indicator
        let status = "Clean";
        if (failedSubstances.length > 0) {
          status = `Failed - ${failedSubstances.join(", ")}`;
        }

        return [formattedDate, status, details];
      });

      // Add table with updated position and improved styling
      autoTable(doc, {
        head: [["Date & Time", "Status", "Details"]],
        body: tableData,
        startY: yPosition + 12,
        styles: {
          fontSize: 10,
          cellPadding: 5,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [240, 245, 250],
        },
        columnStyles: {
          0: { cellWidth: 45 }, // Date column
          1: { cellWidth: 40, fontStyle: "bold" }, // Status column
          2: { cellWidth: "auto" }, // Details column
        },
        willDrawCell: function (data) {
          // Color the Status cell based on the value
          if (data.column.index === 1 && data.cell.text) {
            const cellText = data.cell.text.join("");
            if (cellText.includes("Failed")) {
              data.cell.styles.textColor = [220, 50, 50]; // Red for failed
            } else if (cellText === "Clean") {
              data.cell.styles.textColor = [46, 184, 46]; // Green for clean
            }
          }
        },
      });
    } else {
      // Original code without header
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      const title = `UA & Breathalyzer History for ${selectedResident}`;
      doc.text(title, 14, 20);

      // Add date range if filtering
      if (startDate && endDate) {
        doc.setFontSize(10);
        doc.setFont(undefined, "italic");
        const dateRangeText = `Date Range: ${new Date(
          startDate
        ).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`;
        doc.text(dateRangeText, 14, 28);
      }

      // Process history results to create better formatted table data
      const tableData = historyResults.map((result) => {
        // Format date nicely
        const date = new Date(result.date);
        const formattedDate = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });

        // For breathalyzer results
        if (result.type === "breathalyzer") {
          const status = parseFloat(result.bacLevel) > 0 ? "Failed" : "Clean";
          return [
            formattedDate,
            "Breathalyzer",
            `BAC Level: ${result.bacLevel} - ${status}`,
          ];
        }

        // Create separate lists of passed and failed substances
        const passedSubstances = [];
        const failedSubstances = [];

        Object.entries(result.results)
          .filter(([substance]) => availableSubstances[substance] === true) // Only include enabled substances
          .forEach(([substance, status]) => {
            const substanceName =
              getSubstanceDisplayName(substance).toUpperCase();

            if (status.toLowerCase() === "pass") {
              passedSubstances.push(substanceName);
            } else if (status.toLowerCase() === "fail") {
              failedSubstances.push(substanceName);
            }
          });

        // Format the details text with clear separation
        let details = "";

        if (passedSubstances.length > 0) {
          details += `PASSED: ${passedSubstances.join(", ")}`;
        }

        if (failedSubstances.length > 0) {
          if (details) details += "\n\n";
          details += `FAILED: ${failedSubstances.join(", ")}`;
        }

        // Create a clear status indicator
        let status = "Clean";
        if (failedSubstances.length > 0) {
          status = `Failed - ${failedSubstances.join(", ")}`;
        }

        return [formattedDate, status, details];
      });

      // Improved table styling
      autoTable(doc, {
        head: [["Date & Time", "Status", "Details"]],
        body: tableData,
        startY: startDate && endDate ? 35 : 30,
        styles: {
          fontSize: 10,
          cellPadding: 5,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [240, 245, 250],
        },
        columnStyles: {
          0: { cellWidth: 45 }, // Date column
          1: { cellWidth: 40, fontStyle: "bold" }, // Status column
          2: { cellWidth: "auto" }, // Details column
        },
        willDrawCell: function (data) {
          // Color the Status cell based on the value
          if (data.column.index === 1 && data.cell.text) {
            const cellText = data.cell.text.join("");
            if (cellText.includes("Failed")) {
              data.cell.styles.textColor = [220, 50, 50]; // Red for failed
            } else if (cellText === "Clean") {
              data.cell.styles.textColor = [46, 184, 46]; // Green for clean
            }
          }
        },
      });
    }

    doc.save(
      `History_${selectedResident}_${
        new Date().toISOString().split("T")[0]
      }.pdf`
    );

    return doc;
  };

  const getSubstanceDisplayName = (substanceKey) => {
    if (ALL_SUBSTANCES[substanceKey]) {
      return `${substanceKey} - ${ALL_SUBSTANCES[substanceKey].name}`;
    }
    // Capitalize first letter as fallback
    return substanceKey.charAt(0).toUpperCase() + substanceKey.slice(1);
  };

  const getSubstanceType = (substanceKey) => {
    return ALL_SUBSTANCES[substanceKey]?.type || "drug";
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const renderHistoryTab = () => (
    <div className="bg-slate-800/30 rounded-lg shadow-md p-4 sm:p-6">
      <h2 className="text-cyan-50 font-semibold mb-4">
        UA & Breathalyzer History
      </h2>

      <div className="mb-4">
        <label htmlFor="resident" className="block text-slate-400 mb-2">
          Select Resident
        </label>
        <select
          id="resident"
          value={selectedResident}
          onChange={handleResidentSelection}
          className="w-full px-4 py-3 bg-slate-800/50 backdrop-blur-xl text-white rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-all"
        >
          <option value="">-- Select Resident --</option>
          {[...(organizationData?.residents || [])]
            .filter((resident) => resident.currentResident)
            .sort((a, b) => a.fullName.localeCompare(b.fullName)) // Sort alphabetically
            .map((resident) => (
              <option
                key={resident.id}
                value={resident.fullName}
                className="text-black"
              >
                {resident.fullName}
              </option>
            ))}
        </select>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="flex-1">
          <label htmlFor="start-date" className="block text-slate-400 mb-2">
            Start Date
          </label>
          <input
            id="start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-4 py-3 bg-slate-800/50 backdrop-blur-xl text-white rounded-xl border border-slate-700/50 transition-all"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="end-date" className="block text-slate-400 mb-2">
            End Date
          </label>
          <input
            id="end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-4 py-3 bg-slate-800/50 backdrop-blur-xl text-white rounded-xl border border-slate-700/50 transition-all"
          />
        </div>
      </div>

      <button
        onClick={handleFilter}
        className="w-full sm:w-auto bg-blue-700 hover:bg-blue-800 text-white font-medium py-2 px-4 rounded-lg transition"
      >
        Filter
      </button>

      {historyResults.length > 0 ? (
        <div className="mt-4 space-y-4">
          {historyResults.map((result, idx) => (
            <div
              key={idx}
              className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/50"
            >
              <p className="text-white text-sm">
                <strong>Date:</strong> {new Date(result.date).toLocaleString()}
              </p>
              {result.type === "breathalyzer" ? (
                <>
                  <p className="text-white text-sm">
                    <strong>Type:</strong> Breathalyzer
                  </p>
                  <p className="text-white text-sm">
                    <strong>Summary:</strong> {result.summary}
                  </p>
                  <p className="text-white text-sm mt-2">
                    <strong>BAC Level:</strong>{" "}
                    <span
                      className={
                        parseFloat(result.bacLevel) > 0
                          ? "text-red-500"
                          : "text-cyan-400"
                      }
                    >
                      {result.bacLevel}
                    </span>
                  </p>
                </>
              ) : (
                <>
                  <p className="text-white text-sm">
                    <strong>Type:</strong> UA
                  </p>
                  <p className="text-white text-sm">
                    <strong>Summary:</strong> {result.summary}
                  </p>
                  <p className="text-white text-sm">
                    <strong>Details:</strong>
                  </p>
                  {/* Group substances by type for display */}
                  <div className="mt-2">
                    <div className="mb-2">
                      <ul className="list-disc list-inside text-slate-400 text-sm">
                        {Object.entries(result.results)
                          .filter(
                            ([substance]) =>
                              getSubstanceType(substance) === "drug" &&
                              availableSubstances[substance] === true
                          )
                          .map(([substance, status]) => (
                            <li key={substance}>
                              {getSubstanceDisplayName(substance)}:{" "}
                              <span
                                className={
                                  status === "fail"
                                    ? "text-red-500"
                                    : "text-cyan-400"
                                }
                              >
                                {status}
                              </span>
                            </li>
                          ))}
                      </ul>
                    </div>

                    {/* Only show adulterants section if there are adulterants */}
                    {Object.entries(result.results).some(
                      ([substance]) =>
                        getSubstanceType(substance) === "adulterant"
                    ) && (
                      <div>
                        <p className="text-white text-sm mt-2">
                          <strong>Adulterants:</strong>
                        </p>
                        <ul className="list-disc list-inside text-slate-400 text-sm">
                          {Object.entries(result.results)
                            .filter(
                              ([substance]) =>
                                getSubstanceType(substance) === "adulterant"
                            )
                            .map(([substance, status]) => (
                              <li key={substance}>
                                {getSubstanceDisplayName(substance)}:{" "}
                                <span
                                  className={
                                    status === "fail"
                                      ? "text-red-500"
                                      : "text-cyan-400"
                                  }
                                >
                                  {status}
                                </span>
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-slate-400">
          No results available for the selected resident.
        </p>
      )}

      {historyResults.length > 0 && (
        <button
          onClick={generatePDF}
          disabled={!checkPermission("exportResidentData")}
          className={`mt-4 w-full ${
            checkPermission("exportResidentData")
              ? "bg-cyan-500 hover:bg-cyan-600"
              : "bg-gray-500 cursor-not-allowed"
          } text-white font-medium py-2 px-4 rounded-lg transition`}
        >
          Save PDF
        </button>
      )}
    </div>
  );

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setUaResults((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubstanceCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setTempSubstances((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const currentDate = (day) => {
    const today = new Date();
    // Convert to local midnight
    const localToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const dayIndex = daysOfWeek.indexOf(day);
    const currentDay = localToday.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1;

    const targetDate = new Date(localToday);
    targetDate.setDate(
      localToday.getDate() - currentDay + mondayOffset + dayIndex
    );

    return targetDate.toISOString().split("T")[0];
  };

  const handleSubmit = async () => {
    if (isSubmitting || !selectedResident) {
      if (!selectedResident) alert("Please select a resident");
      return;
    }

    try {
      setIsSubmitting(true);
      const now = new Date();
      // Ensure we're using local timezone for the date
      const localNow = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      );

      // Find resident ID from the name
      const selectedResidentObj = organizationData?.residents.find(
        (resident) => resident.fullName === selectedResident
      );

      if (!selectedResidentObj || !selectedResidentObj.id) {
        alert("Could not find resident ID. Please try again.");
        setIsSubmitting(false);
        return;
      }

      const resultDocument = {
        residentFullName: selectedResident,
        date: localNow.toISOString(),
        weekIdentifier: `Week-${weekNumber}-${localNow.getFullYear()}`,
        results: Object.fromEntries(
          Object.entries(uaResults).map(([substance, value]) => [
            substance,
            value ? "fail" : "pass",
          ])
        ),
        summary:
          Object.keys(uaResults)
            .filter((key) => uaResults[key])
            .map((key) => ALL_SUBSTANCES[key]?.name || key)
            .join(", ") || "Clean",
      };

      // New path using resident subcollection
      const uaResultRef = doc(
        db,
        `organizations/${organizationId}/residents/${
          selectedResidentObj.id
        }/uaResults/${Date.now()}`
      );

      await setDoc(uaResultRef, resultDocument);

      // Update UI with new result
      setSubmittedUAs((prev) => [...prev, resultDocument]);
      alert("UA result submitted successfully");
      setSelectedResident("");

      // Reset all results to false
      const resetResults = {};
      Object.keys(uaResults).forEach((key) => {
        resetResults[key] = false;
      });
      setUaResults(resetResults);

      setActiveTab("schedule");
    } catch (err) {
      console.error("Error submitting UA result:", err);
      alert("Failed to submit UA result");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDayCard = (day) => {
    const dayDate = currentDate(day);

    return (
      <div key={day} className="bg-slate-800/30 rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-blue-50 font-bold text-lg md:text-xl">{day}</h3>
          <span className="text-slate-400 text-sm">{dayDate}</span>
        </div>
        {Object.entries(organizationData?.housesData || {}).map(
          ([houseName, houseData]) =>
            weeklySchedule?.[houseName]?.[day]?.length > 0 ? (
              <div key={houseName} className="mb-3">
                <h4 className="text-slate-400 text-sm font-medium">
                  {houseName}
                </h4>
                {weeklySchedule[houseName][day].map((resident, idx) => {
                  const isSubmitted = submittedUAs.some((ua) => {
                    const scheduleDateNormalized = dayDate;
                    const submittedDateNormalized = getLocalDateString(ua.date);
                    const residentNormalized = resident
                      .replace(/\s\(\d+\)$/, "")
                      .trim()
                      .toLowerCase();
                    const submittedResidentNormalized = ua.residentFullName
                      .replace(/\s\(\d+\)$/, "")
                      .trim()
                      .toLowerCase();

                    return (
                      scheduleDateNormalized === submittedDateNormalized &&
                      residentNormalized === submittedResidentNormalized
                    );
                  });

                  return (
                    <div
                      key={idx}
                      className={`group relative rounded-lg p-2 border ${
                        isSubmitted
                          ? "border-cyan-600 bg-cyan-800/40 backdrop-blur-sm"
                          : "border-slate-700/50 bg-slate-900/50"
                      } mb-1 flex justify-between items-center cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-blue-200/10 hover:border-blue-200/20`}
                      onClick={() => handleResidentClick(resident)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" ? handleResidentClick(resident) : null
                      }
                    >
                      <span className="text-white text-sm">{resident}</span>
                      {isSubmitted && (
                        <span className="text-cyan-500 flex items-center">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </span>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-b from-cyan-600/5 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300" />
                    </div>
                  );
                })}
              </div>
            ) : null
        )}
        {!Object.keys(organizationData?.housesData || {}).some(
          (house) => weeklySchedule?.[house]?.[day]?.length
        ) && <div className="text-slate-500 text-sm">No UAs scheduled</div>}
      </div>
    );
  };

  // Add this improved normalization function at the same level as the existing normalizeName function
  const normalizeNameForComparison = (name) => {
    // Remove any numbers in parentheses, extra spaces, and convert to lowercase for case-insensitive comparison
    return name
      .replace(/\s\(\d+\)$/, "")
      .trim()
      .toLowerCase();
  };

  // Then update the handleResidentClick function
  const handleResidentClick = (resident) => {
    if (checkPermission("addUa")) {
      // Normalize the clicked resident name
      const normalizedClickedName = normalizeNameForComparison(resident);

      // Find the matching resident using the improved normalization for both names
      const actualResident = organizationData?.residents.find(
        (r) => normalizeNameForComparison(r.fullName) === normalizedClickedName
      );

      if (actualResident) {
        setSelectedResident(actualResident.fullName); // Use the exact name from Firebase
        setActiveTab("collect");

        // Debug logging to verify the match works
        console.log("Found resident match:", actualResident.fullName);
      } else {
        console.error("Resident not found:", resident);
        console.log(
          "Available residents:",
          organizationData?.residents.map((r) => r.fullName)
        );
      }
    }
  };

  const openSubstanceModal = () => {
    setTempSubstances({ ...availableSubstances });
    setShowSubstanceModal(true);
  };

  const closeSubstanceModal = () => {
    setShowSubstanceModal(false);
  };

  const saveSubstances = async () => {
    try {
      const houseSettingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );

      // First get the current document to preserve other fields
      const docSnap = await getDoc(houseSettingsRef);
      let currentData = {};

      if (docSnap.exists()) {
        currentData = docSnap.data();
      }

      // Update with new substances
      await setDoc(
        houseSettingsRef,
        {
          ...currentData,
          substances: tempSubstances,
        },
        { merge: true }
      );

      setAvailableSubstances(tempSubstances);

      // Update uaResults to only include available substances
      const newUaResults = {};
      Object.keys(tempSubstances).forEach((substance) => {
        if (tempSubstances[substance]) {
          newUaResults[substance] = uaResults[substance] || false;
        }
      });
      setUaResults(newUaResults);

      closeSubstanceModal();
      alert("Substance list updated successfully");
    } catch (err) {
      console.error("Error saving substance list:", err);
      alert("Failed to save substance list");
    }
  };

  const renderSubstanceModal = () => {
    if (!showSubstanceModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-900 rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto shadow-2xl">
          <div className="p-6 border-b border-slate-700">
            <h3 className="text-xl font-bold text-white">Substance List</h3>
            <p className="text-slate-400 text-sm mt-1">
              Select which substances your organization tests for
            </p>
          </div>

          <div className="p-6 max-h-[50vh] overflow-y-auto">
            {/* Primary Substances */}
            <h4 className="text-lg font-medium text-white mb-3">Substances</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {Object.keys(ALL_SUBSTANCES)
                .filter((key) => ALL_SUBSTANCES[key].type === "drug")
                .map((key) => (
                  <div key={key} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`substance-${key}`}
                      name={key}
                      checked={tempSubstances[key] || false}
                      onChange={handleSubstanceCheckboxChange}
                      className="mr-2"
                    />
                    <label
                      htmlFor={`substance-${key}`}
                      className="text-slate-300"
                    >
                      <strong>{key}</strong> - {ALL_SUBSTANCES[key].name}
                    </label>
                  </div>
                ))}
            </div>

            {/* Adulterants Section */}
            <h4 className="text-lg font-medium text-white mb-3 mt-6 border-t border-slate-700 pt-4">
              Adulterants
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.keys(ALL_SUBSTANCES)
                .filter((key) => ALL_SUBSTANCES[key].type === "adulterant")
                .map((key) => (
                  <div key={key} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`substance-${key}`}
                      name={key}
                      checked={tempSubstances[key] || false}
                      onChange={handleSubstanceCheckboxChange}
                      className="mr-2"
                    />
                    <label
                      htmlFor={`substance-${key}`}
                      className="text-slate-300"
                    >
                      <strong>{key}</strong> - {ALL_SUBSTANCES[key].name}
                    </label>
                  </div>
                ))}
            </div>
          </div>

          <div className="p-6 border-t border-slate-700 flex flex-wrap gap-4 justify-end">
            <button
              onClick={closeSubstanceModal}
              className="px-5 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
            >
              Cancel
            </button>
            <button
              onClick={saveSubstances}
              className="px-5 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 transition"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCollectTab = () => {
    const enabledSubstances = Object.keys(availableSubstances).filter(
      (key) => availableSubstances[key]
    );

    return (
      <div className="bg-slate-800/30 rounded-lg shadow-md p-6">
        <h2 className="text-blue-50 font-semibold mb-4">Collect UA Sample</h2>

        {enabledSubstances.length === 0 ? (
          <div className="bg-yellow-800/30 p-4 rounded-lg mb-4">
            <p className="text-yellow-300">
              No substances configured. Please click the "Substance List" button
              at the top to set up the substances your organization tests for.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label htmlFor="resident" className="block text-slate-400 mb-2">
                Select Resident
              </label>
              <select
                id="resident"
                value={selectedResident}
                onChange={handleResidentSelection}
                className="w-full px-4 py-3 bg-slate-800/50 backdrop-blur-xl text-white rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-all"
              >
                <option value="">-- Select Resident --</option>
                {[...(organizationData?.residents || [])]
                  .filter((resident) => resident.currentResident)
                  .sort((a, b) => a.fullName.localeCompare(b.fullName)) // Sort alphabetically
                  .map((resident) => (
                    <option
                      key={resident.id}
                      value={resident.fullName}
                      className="text-black"
                    >
                      {resident.fullName}
                    </option>
                  ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-slate-400 mb-2">
                Check any substances found:
              </label>
              {/* Drug Substances */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {enabledSubstances
                  .filter(
                    (substance) => ALL_SUBSTANCES[substance]?.type === "drug"
                  )
                  .map((substance) => (
                    <div key={substance} className="flex items-center">
                      <input
                        type="checkbox"
                        id={substance}
                        name={substance}
                        checked={uaResults[substance] || false}
                        onChange={handleCheckboxChange}
                        className="mr-2"
                      />
                      <label htmlFor={substance} className="text-slate-300">
                        <span className="font-medium">{substance}</span>
                        <span className="text-sm ml-1">
                          - {ALL_SUBSTANCES[substance]?.name}
                        </span>
                      </label>
                    </div>
                  ))}
              </div>

              {/* Adulterants Section - only show if there are adulterants enabled */}
              {enabledSubstances.some(
                (substance) => ALL_SUBSTANCES[substance]?.type === "adulterant"
              ) && (
                <>
                  <label className="block text-slate-400 mb-2 border-t border-slate-700 pt-4 mt-4">
                    Adulterants:
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {enabledSubstances
                      .filter(
                        (substance) =>
                          ALL_SUBSTANCES[substance]?.type === "adulterant"
                      )
                      .map((substance) => (
                        <div key={substance} className="flex items-center">
                          <input
                            type="checkbox"
                            id={substance}
                            name={substance}
                            checked={uaResults[substance] || false}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                          />
                          <label htmlFor={substance} className="text-slate-300">
                            <span className="font-medium">{substance}</span>
                            <span className="text-sm ml-1">
                              - {ALL_SUBSTANCES[substance]?.name}
                            </span>
                          </label>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !checkPermission("addUa")}
                className={`w-full ${
                  isSubmitting
                    ? "bg-gray-500 cursor-not-allowed"
                    : checkPermission("addUa")
                    ? "bg-blue-500 hover:bg-blue-600"
                    : "bg-gray-500 cursor-not-allowed"
                } text-white font-medium py-2 px-4 rounded-lg transition`}
              >
                {isSubmitting ? "Submitting..." : "Submit UA Result"}
              </button>

              <button
                onClick={() =>
                  selectedResident
                    ? setShowBreathalyzerModal(true)
                    : alert("Please select a resident")
                }
                disabled={!checkPermission("addUa")}
                className={`w-full ${
                  checkPermission("addUa")
                    ? "bg-green-500 hover:bg-green-600"
                    : "bg-gray-500 cursor-not-allowed"
                } text-white font-medium py-2 px-4 rounded-lg transition`}
              >
                Record Breathalyzer
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "schedule":
        return checkPermission("viewUa") && checkFeature("uaScheduling") ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {daysOfWeek.map(renderDayCard)}
          </div>
        ) : null;
      case "collect":
        return checkPermission("addUa") && checkFeature("uaRecording")
          ? renderCollectTab()
          : null;
      case "history":
        return checkPermission("viewUaHistory") ? renderHistoryTab() : null;
      default:
        return null;
    }
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-[#18181B] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>UA Management</title>
      </Helmet>
      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-4 mb-12">
            <div className="flex items-center gap-6 w-full md:w-auto">
              <div className="flex gap-3">
                <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
                <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
                <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  UA Management
                </h1>
              </div>
            </div>

            {/* Substance List Button */}
            <button
              onClick={openSubstanceModal}
              className="flex w-full md:w-auto items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all shadow-lg"
            >
              <List className="w-4 h-4" />
              Substance List
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              {tabs
                .filter((tab) => checkPermission(tab.permission))
                .map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full md:w-auto px-6 py-3 font-medium flex items-center justify-center gap-2 rounded-xl transition-all duration-300 shadow-lg ${
                      activeTab === tab.id
                        ? "bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white hover:shadow-cyan-500/25"
                        : "bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white hover:shadow-slate-500/25"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
            </div>
          </div>
          {renderTabContent()}
        </div>
      </div>

      {/* Render Substance Modal */}
      {renderSubstanceModal()}
      {renderBreathalyzerModal()}
    </>
  );
};

export default UA;
